import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { Check, ArrowRight, Shield, AlertCircle } from "lucide-react";

/**
 * Public landing page reached via the invite email link.
 * - GET /api/staff-invites/{token} fetches summary (email + role + business)
 * - POST /api/staff-invites/accept creates the user + redirects to /login
 */
export default function AcceptStaffInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Token mancante nell'URL."); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/staff-invites/${encodeURIComponent(token)}`);
        setInfo(data);
        setName(data.staff_name || "");
      } catch (ex) {
        setErr(formatErr(ex.response?.data?.detail) || "Invito non valido o scaduto.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("La password deve avere almeno 8 caratteri."); return; }
    if (password !== pw2) { setErr("Le password non coincidono."); return; }
    setBusy(true);
    try {
      await api.post("/staff-invites/accept", { token, password, name: name.trim() || undefined });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Errore durante l'accettazione.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-zinc-900 rounded-3xl shadow-xl border border-zinc-100 dark:border-zinc-800 overflow-hidden" data-testid="accept-invite-card">
          {loading && (
            <div className="p-10 text-center text-sm text-zinc-500 dark:text-zinc-400">Verifico l'invito…</div>
          )}

          {!loading && err && !info && (
            <div className="p-8 text-center">
              <div className="h-12 w-12 mx-auto rounded-full bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-300 flex items-center justify-center mb-3">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="font-display text-xl font-medium dark:text-zinc-100">Invito non valido</div>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2">{err}</p>
              <Link to="/" className="btn-secondary mt-6 inline-flex">Torna alla home</Link>
            </div>
          )}

          {info && !done && (
            <>
              <div className="bg-gradient-to-br from-blue-600 to-violet-600 text-white p-6">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-90 flex items-center gap-1.5">
                  <Shield className="h-3 w-3" /> Invito staff
                </div>
                <div className="font-display text-2xl font-semibold mt-1 leading-tight">Accedi a {info.business_name}</div>
                <div className="text-sm opacity-90 mt-1">
                  Ti è stato assegnato il ruolo <strong>{info.access_role === "manager" ? "Responsabile" : "Staff"}</strong>.
                </div>
              </div>

              <form className="p-6 space-y-4" onSubmit={submit}>
                <Field label="Email" hint="non modificabile">
                  <input type="email" className="input-base" value={info.email} disabled data-testid="accept-input-email" />
                </Field>
                <Field label="Il tuo nome">
                  <input
                    type="text" className="input-base" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Es. Sara Bianchi" maxLength={80} data-testid="accept-input-name"
                  />
                </Field>
                <Field label="Crea password" required>
                  <input
                    type="password" className="input-base" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Almeno 8 caratteri" minLength={8} required data-testid="accept-input-password"
                  />
                </Field>
                <Field label="Conferma password" required>
                  <input
                    type="password" className="input-base" value={pw2} onChange={(e) => setPw2(e.target.value)}
                    placeholder="Ripeti la password" minLength={8} required data-testid="accept-input-password2"
                  />
                </Field>

                {err && (
                  <div className="text-sm text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 rounded-lg" data-testid="accept-error">{err}</div>
                )}

                <button type="submit" disabled={busy} className="btn-accent w-full justify-center disabled:opacity-60" data-testid="accept-submit">
                  {busy ? "Attivo l'accesso…" : <>Attiva accesso <ArrowRight className="h-4 w-4 ml-2" /></>}
                </button>
                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 text-center">
                  Lo staff può accedere solo alle aree consentite. Le impostazioni legali, API e fatturazione restano riservate al proprietario.
                </p>
              </form>
            </>
          )}

          {done && (
            <div className="p-10 text-center" data-testid="accept-success">
              <div className="h-14 w-14 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center mb-4">
                <Check className="h-7 w-7" strokeWidth={3} />
              </div>
              <div className="font-display text-xl font-medium dark:text-zinc-100">Accesso attivato</div>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2">Ti reindirizziamo alla pagina di login…</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .input-base {
          width: 100%; height: 44px; padding: 0 14px; border-radius: 12px;
          background: rgba(244, 244, 245, 1); border: 1px solid rgba(228, 228, 231, 1);
          font-size: 14px; color: rgb(24, 24, 27);
        }
        .input-base:focus { outline: 2px solid rgb(37, 99, 235); outline-offset: -1px; border-color: transparent; }
        .input-base:disabled { opacity: 0.6; }
        .dark .input-base { background: rgb(39, 39, 42); border-color: rgb(63, 63, 70); color: rgb(244, 244, 245); }
      `}</style>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 flex items-center justify-between">
        <span>{label}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
        {hint && <span className="text-zinc-400 dark:text-zinc-500 text-[11px]">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
